import { createContext, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ViewListIcon from '@mui/icons-material/ViewList';
import { Link as RouterLink } from 'react-router-dom';
import { GameStatus } from '../../constants/gameConstants';

export const statusLabel = (status) => {
  switch (status) {
    case GameStatus.NOT_STARTED:
      return 'Not started';
    case GameStatus.IN_PROGRESS:
      return 'Live';
    case GameStatus.COMPLETE:
      return 'Complete';
    case GameStatus.PLAYOFF:
      return 'Tiebreaker';
    default:
      return 'Unknown';
  }
};

export const statusColor = (status) => {
  switch (status) {
    case GameStatus.IN_PROGRESS:
      return 'success';
    case GameStatus.PLAYOFF:
      return 'warning';
    case GameStatus.COMPLETE:
      return 'default';
    default:
      return 'default';
  }
};

// Pages hand their action buttons to the layout. On desktop they sit in the
// app bar; on phones they move into the hamburger drawer so the bar keeps a
// single, understandable button.
export const PortalActionsSlotContext = createContext(null);

export const PortalActions = ({ children }) => {
  const slots = useContext(PortalActionsSlotContext);
  const [container, setContainer] = useState(null);

  useEffect(() => {
    setContainer((slots?.compact ? slots.mobile : slots?.desktop) || null);
  }, [slots]);

  return container ? createPortal(children, container) : null;
};

// A page's primary action stays visible in the app bar on phones, next to the
// menu button, instead of being buried in the drawer.
export const PortalHeaderAction = ({ children }) => {
  const slots = useContext(PortalActionsSlotContext);
  const [container, setContainer] = useState(null);

  useEffect(() => {
    setContainer((slots?.compact ? slots.header : slots?.desktop) || null);
  }, [slots]);

  return container ? createPortal(children, container) : null;
};

export const PortalPageHeader = ({ title, subtitle, backTo, backLabel, action, barAction }) => (
  <Box mb={2}>
    {backTo && (
      <Button
        component={RouterLink}
        to={backTo}
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 1, ml: -1 }}>
        {backLabel || 'Back'}
      </Button>
    )}
    <Box display="flex" alignItems="flex-start" gap={1}>
      <Box flex={1} minWidth={0}>
        <Typography variant="h5" fontWeight="bold">
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
      {action && <PortalActions>{action}</PortalActions>}
      {barAction && <PortalHeaderAction>{barAction}</PortalHeaderAction>}
    </Box>
  </Box>
);

export const PortalPrintActions = ({ rinkTo, sectionTo }) => {
  const actions = [
    {
      key: 'rinks',
      to: rinkTo,
      label: 'Print rink QR codes',
      Icon: QrCode2Icon
    },
    {
      key: 'sections',
      to: sectionTo,
      label: 'Print section QR codes',
      Icon: ViewListIcon
    }
  ];

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      flexWrap="wrap"
      useFlexGap
      justifyContent="flex-end"
      sx={{ width: { xs: '100%', sm: 'auto' } }}>
      {actions.map(({ key, to, label, Icon }) => (
        <Button
          key={key}
          component={RouterLink}
          to={to}
          variant="outlined"
          size="small"
          startIcon={<Icon />}
          sx={{
            width: { xs: '100%', sm: 'auto' },
            justifyContent: 'flex-start',
            whiteSpace: 'nowrap'
          }}>
          {label}
        </Button>
      ))}
    </Stack>
  );
};

export const PortalLoading = () => (
  <Box display="flex" justifyContent="center" py={6}>
    <CircularProgress />
  </Box>
);

export const PortalError = ({ message, onRetry }) => (
  <Paper sx={{ p: 2 }} elevation={2}>
    <Alert severity="error" sx={{ mb: onRetry ? 2 : 0 }}>
      {message || 'Something went wrong. Please try again.'}
    </Alert>
    {onRetry && (
      <Button variant="contained" onClick={onRetry}>
        Try again
      </Button>
    )}
  </Paper>
);

export const PortalEmpty = ({ children }) => (
  <Paper sx={{ p: 3 }} elevation={1}>
    <Typography color="text.secondary" textAlign="center">
      {children}
    </Typography>
  </Paper>
);

export const PortalCounts = ({ total, live, complete }) => (
  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
    <Chip size="small" label={`${total} ${total === 1 ? 'game' : 'games'}`} />
    {live > 0 && <Chip size="small" color="success" label={`${live} live`} />}
    {complete > 0 && <Chip size="small" variant="outlined" label={`${complete} complete`} />}
  </Stack>
);
